import React, { useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {
    DataGridPremium,
    GridColDef,
    useGridApiRef,
    gridAggregationLookupSelector,
} from '@mui/x-data-grid-premium';

import { ICounterpart } from 'CommonTypes/Tasks/Counterparties/IResponse';
import { IServiceValue, TResponse } from 'CommonTypes/Tasks/Services/TResponse';
import { IService } from 'CommonTypes/Tasks/Services/IService';
import useEnqueueSnackbar from 'Lib/Notistack/useEnqueueSnackbar';
import MenuAction from './components/MenuAction';
import getRows from './services/getRows.js';
import getRateVat from './services/getRateVat.js';
import saveTaskPrice from './utils/saveTaskPrice';
import getCounterparties from './services/getCounterparties';
import postCurrentCompany from './services/postCurrentCompany';
import postCurrentCounterparty from './services/postCurrentCounterparty';
import CustomNoRowsOverlay from './components/CustomNoRowsOverlay';
import addTaskPrice from './services/addTaskPrice.js';
import getServicesByFilter from './services/getServicesByFilter';
import getServiceByServiceId from './services/getServiceByServiceId';

import { TCounterparties } from './types/TCounterparties';
import { IRateVatList } from './types/IRateVatList';

interface INewService {
    sum: number;
    ids: [number];
}

function DataGrid({ taskId }: { taskId: number }) {
    const apiRef = useGridApiRef();

    const [rows, setRows] = useState<IService[] | []>([]);
    const [rateVatList, setRateVatList] = useState([]);
    const [counterparties, setCounterparties] = useState<TCounterparties | []>([]);
    const [camponies, setCamponies] = useState<TCounterparties | []>([]);
    const [
        currentCompany,
        setCurrentCompany,
    ] = useState<ICounterpart | null>(null);
    const [
        currentCounterparty,
        setCurrentCounterparties,
    ] = useState<ICounterpart | null>(null);
    const [companyLoading, setCompanyLoading] = useState(true);
    const [counterpartyLoading, setCounterpartyLoading] = useState(true);
    const [project, setProject] = useState(0);
    const { success, error } = useEnqueueSnackbar();
    // const [sumAll, setSumAll] = useState<number | string>(0);
    // setSumAll(rows.map(item => item.total).reduce((prev, curr) => +prev + (+curr), 0));

    useEffect(() => {
        getRateVat()
            .then((res) => {
                setRateVatList(res);
            });

        getRows(taskId)
            .then((res) => {
                setRows(res);
            });

        getCounterparties(taskId)
            .then((res) => {
                setCounterparties(res.counterparties);
                const firm = res.counterparties.filter(counterparty => !!counterparty.firm);
                setCamponies(firm);
                setProject(res.projectId);
                const [counterparty] = res.counterparties.filter(el => !!+el.currentCounterparty);
                if (counterparty) setCurrentCounterparties(counterparty);
                const [company] = res.counterparties.filter(el => !!+el.currentCompany);
                if (company) setCurrentCompany(company);
                setCompanyLoading(false);
                setCounterpartyLoading(false);
            });
    }, []);

    const handleOnChangeCompany = (value: ICounterpart | null) => {
        setCompanyLoading(true);
        postCurrentCompany({ taskId, companyId: value!.id })
            .then((res) => {
                if (typeof res === 'boolean' && res) {
                    setCurrentCompany(value);
                    setCompanyLoading(false);
                    return success('Фирма успешно обновлена');
                }
                setCompanyLoading(false);
                return error('Не удалось обновить фирму');
            });
    };

    const handleOnChangeCounterparty = (value: ICounterpart | null) => {
        setCounterpartyLoading(true);
        postCurrentCounterparty({ taskId, counterpartyId: value!.id })
            .then((res) => {
                if (typeof res === 'boolean' && res) {
                    setCurrentCounterparties(value);
                    setCounterpartyLoading(false);
                    return success('Контрагент успешно обновлен');
                }
                setCounterpartyLoading(false);
                return error('Не удалось обновить контрагента');
            });
    };

    const valueOptions = rateVatList.map((el: IRateVatList) => ({
        value: el.id,
        label: el.name,
    }));

    const handleDeleteRow = (idRow: string) => {
        apiRef.current.updateRows([{ id: idRow, _action: 'delete' }]);
    };

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: 'Наименование',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            aggregable: false,
            renderCell: (params) => {
                if (params.id === 'auto-generated-group-footer-root') {
                    return '';
                }
                return (
                    <p>
                        <span className="color-blue">{params.row.article ? `${params.row.article}:` : ''}</span>
                        {' '}
                        {params.row.fullname || ''}
                        {' '}
                        (
                        {params.row.ei || ''}
                        )
                    </p>
                );
            },
        },
        {
            field: 'count',
            headerName: 'Кол-во.',
            headerAlign: 'right',
            align: 'right',
            type: 'number',
            editable: true,
            aggregable: false,
            valueGetter: (params) => {
                if (params.id === 'auto-generated-group-footer-root') {
                    return '';
                }
                return (+params.value).toFixed(2);
            },
        },
        {
            field: 'price',
            headerName: 'Цена прод.',
            headerAlign: 'right',
            align: 'right',
            type: 'number',
            editable: true,
            aggregable: false,
            valueGetter: (params) => {
                if (params.id === 'auto-generated-group-footer-root') {
                    return '';
                }
                return Number((+params.value).toFixed(2));
            },
        },
        {
            field: 'discount_pct',
            headerName: 'Скидка, %',
            headerAlign: 'right',
            align: 'right',
            type: 'number',
            editable: true,
            aggregable: false,
            hide: true,
            valueGetter: (params) => {
                if (params.id === 'auto-generated-group-footer-root') {
                    return '';
                }
                return (+params.value).toFixed(2);
            },
        },
        {
            field: 'discount_price',
            headerName: 'Цена со скидкой',
            headerAlign: 'right',
            align: 'right',
            type: 'number',
            aggregable: false,
            hide: true,
            valueGetter: (params) => {
                const price = +params.row.price;
                const discountPrice = price - (price * (+params.row.discount_pct / 100));
                return +discountPrice.toFixed(2) || '';
            },
        },
        {
            field: 'sum',
            headerName: 'Сумма',
            headerAlign: 'right',
            align: 'right',
            type: 'number',
            aggregable: false,
            valueGetter: (params) => {
                const sum = +params.row.count * (+params.row.price);
                const discountedAmount = sum - (sum * (+params.row.discount_pct / 100));
                const resultSum = +(discountedAmount.toFixed(2));
                return resultSum || '';
            },
        },
        {
            field: 'id_vat',
            headerName: 'Ставка НДС',
            headerAlign: 'right',
            align: 'right',
            type: 'singleSelect',
            editable: true,
            aggregable: false,
            valueOptions,
            valueGetter: ({ value, colDef }) => {
                const option = (colDef.valueOptions as { value: string | number, label: string }[])
                    .find(
                        ({ value: optionValue }) => +value === +optionValue,
                    );
                return option?.label;
            },
        },
        {
            field: 'sum_vat',
            headerName: 'Сумма НДС',
            headerAlign: 'right',
            align: 'right',
            type: 'number',
            aggregable: false,
            valueGetter: (params) => {
                if (params.id === 'auto-generated-group-footer-root') {
                    return '';
                }
                const vat = rateVatList.find(
                    (el: IRateVatList) => +el.id === +params.row.id_vat,
                ) as unknown as IRateVatList;
                const sum = +params.row.sum;
                const percentVat = Number(vat?.rate);

                const sumVat = Number(vat?.in_amount) === 1
                    ? sum * (percentVat / (100 + (percentVat)))
                    : sum * (percentVat / 100);

                return +(sumVat.toFixed(2)) || 0;
            },
        },
        {
            field: 'total',
            headerName: 'Сумма доходов',
            headerAlign: 'right',
            align: 'right',
            type: 'number',
            valueGetter: (params) => {
                const vat = rateVatList.find(
                    (el: IRateVatList) => +el.id === +params.row.id_vat,
                ) as unknown as IRateVatList;
                const percentVat = Number(vat?.rate);
                const vatInAmount = Number(vat?.in_amount) === 1;

                const sum = +params.row.count * (+params.row.price);
                const discountedAmount = sum - (sum * (+params.row.discount_pct / 100));

                const sumVat = vatInAmount
                    ? discountedAmount * (percentVat / (100 + (percentVat)))
                    : discountedAmount * (percentVat / 100);

                const income = vatInAmount ? sum : sumVat + sum;

                return Number(income.toFixed(2));
            },
        },
        {
            field: 'purchase_price',
            headerName: 'Цена закупки',
            headerAlign: 'right',
            align: 'right',
            type: 'number',
            editable: true,
            hide: true,
            valueGetter: (params) => {
                if (params.id === 'auto-generated-group-footer-root') {
                    return '';
                }
                return (+params.value).toFixed(2);
            },
        },
        {
            field: 'purchase_vat',
            headerName: 'НДС закупки',
            type: 'singleSelect',
            headerAlign: 'right',
            align: 'right',
            editable: true,
            hide: true,
            valueOptions,
            valueGetter: ({ value, colDef }) => {
                const option = (colDef.valueOptions as { value: string | number, label: string }[])
                    .find(
                        ({ value: optionValue }) => +value === +optionValue,
                    );
                return option?.label;
            },
        },
        {
            field: 'purchase_sum_vat',
            headerName: 'НДС закуп.',
            headerAlign: 'right',
            align: 'right',
            type: 'number',
            hide: true,
            valueGetter: (params) => {
                if (params.id === 'auto-generated-group-footer-root') {
                    return '';
                }
                const vat = rateVatList.find(
                    (el: IRateVatList) => +el.id === +params.row.purchase_vat,
                ) as unknown as IRateVatList;

                const purchasePrice = +params.row.purchase_price;
                const percentVat = Number(vat?.rate);

                const purchaseSumVat = Number(vat?.in_amount) === 1
                    ? purchasePrice * (percentVat / (100 + percentVat))
                    : purchasePrice * (percentVat / 100);

                return Number(purchaseSumVat.toFixed(2)) || 0;
            },
        },
        {
            field: 'purchase',
            headerName: 'Сумма расходов',
            headerAlign: 'right',
            align: 'right',
            type: 'number',
            hide: true,
            valueGetter: (params) => {
                const vat = rateVatList.find(
                    (el: IRateVatList) => +el.id === +params.row.purchase_vat,
                ) as unknown as IRateVatList;

                const purchasePrice = +params.row.purchase_price;
                const count = +params.row.count;
                const percentVat = Number(vat?.rate);
                const vatInAmount = Number(vat?.in_amount) === 1;

                const purchaseSumVat = vatInAmount
                    ? purchasePrice * (percentVat / (100 + percentVat))
                    : purchasePrice * (percentVat / 100);

                const expenses = vatInAmount
                    ? count * purchasePrice
                    : (count * purchasePrice) + (purchaseSumVat);

                return Number(expenses.toFixed(2));
            },
        },
        {
            field: 'action',
            headerName: '',
            align: 'center',
            width: 50,
            resizable: false,
            aggregable: false,
            renderCell: (params) => {
                if (params.id === 'auto-generated-group-footer-root') {
                    return '';
                }
                return (
                    <MenuAction
                        taskId={params.row.id_task}
                        idTaskPrice={params.row.id}
                        name={params.row.name}
                        deleteRow={handleDeleteRow}
                    />
                );
            },
        },
    ];

    const [filter, setFilter] = useState('');
    const [servicesOptins, setOptions] = useState<TResponse | []>([]);
    const [loading, setLoading] = useState(false);
    const [newService, setNewService] = useState(false);
    useEffect(() => {
        if (filter.length > 1) {
            setLoading(true);
            getServicesByFilter(filter)
                .then((res) => {
                    setOptions(res);
                    setLoading(false);
                });
        }
    }, [filter]);

    const handleSearchServices = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setFilter(event.target.value);
    };

    const handleAddService = (
        event: React.SyntheticEvent<Element, Event>,
        value: IServiceValue | null,
    ) => {
        setNewService(true);
        addTaskPrice(
            {
                id_task: taskId,
                prices: [
                    {
                        id: value!.id,
                        count: 1,
                        name: value!.value,
                        project,
                    },
                ],
            },
        ).then((result: INewService) => {
            const { ids } = result;
            const [id] = ids;
            if (!id) {
                setNewService(false);
                return error('Не удалось добавить новую услугу');
            }
            getServiceByServiceId(id)
                .then((res) => {
                    if (res.length) {
                        apiRef.current.updateRows(res);
                        success('Новая услуга успешно добавлена');
                    }
                    setNewService(false);
                });
            return true;
        });
        setOptions([]);
    };

    const processRowUpdate = (newRow: IService) => {
        // console.log(gridAggregationLookupSelector(apiRef.current.state, apiRef.current.instanceId));
        saveTaskPrice(newRow, rateVatList);
        return newRow;
    };

    const handleProcessRowUpdateError = (errors: string) => {
        console.log(errors);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Grid container spacing={1}>
                <Grid item xs={4}>
                    <Autocomplete
                        disablePortal
                        blurOnSelect
                        disableClearable={!!currentCompany}
                        disabled={companyLoading}
                        size="small"
                        value={currentCompany}
                        options={camponies}
                        getOptionLabel={option => option.value}
                        renderOption={(prop, option) => (
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            <li {...prop} key={option.id}>
                                {option.value}
                            </li>
                        )}
                        renderInput={params => (
                            <TextField
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...params}
                                size="small"
                                label="Фирма"
                                variant="standard"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {companyLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        onChange={(event, value) => handleOnChangeCompany(value)}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Autocomplete
                        disablePortal
                        blurOnSelect
                        disabled={counterpartyLoading}
                        disableClearable={!!currentCounterparty}
                        size="small"
                        value={currentCounterparty}
                        options={counterparties}
                        getOptionLabel={option => option.value}
                        renderOption={(prop, option) => (
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            <li {...prop} key={option.id}>
                                {option.value}
                            </li>
                        )}
                        renderInput={params => (
                            <TextField
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...params}
                                size="small"
                                label="Контрагент"
                                variant="standard"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {counterpartyLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        onChange={(event, value) => handleOnChangeCounterparty(value)}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Autocomplete
                        disablePortal
                        blurOnSelect
                        disabled={!currentCompany || newService}
                        size="small"
                        options={servicesOptins}
                        value={null}
                        noOptionsText="Искомая услуга отсутствует"
                        getOptionLabel={option => option.value}
                        renderOption={(prop, option) => (
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            <li {...prop} key={option.id}>
                                {option.value}
                            </li>
                        )}
                        renderInput={params => (
                            <TextField
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...params}
                                size="small"
                                label="Добавить продукт"
                                variant="standard"
                                onChange={event => handleSearchServices(event)}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {newService ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        loading={loading}
                        loadingText="Загрузка искомых услуг"
                        onChange={handleAddService}
                    />
                </Grid>
            </Grid>
            <DataGridPremium
                rows={rows}
                columns={columns}
                components={{
                    NoRowsOverlay: CustomNoRowsOverlay,
                }}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                apiRef={apiRef}
                initialState={{
                    aggregation: {
                        model: {
                            total: 'sum',
                            purchase: 'sum',
                        },
                    },
                }}
                experimentalFeatures={{ aggregation: true, newEditingApi: true }}
            />
        </Box>
    );
}

export default DataGrid;
